import { IOtpApiUserBeanSession, IOtpRawToken } from '@aca-new/app/pages/login/shared/models/interfaces/login-otp-auth.interface';
import { ILoginTokenContent, ILoginTokenResponse, ITokenDetails } from '@aca-new/app/pages/login/shared/models/interfaces/token-response.interface';

/**
 * Normalizes raw OTP token fields into login `ITokenDetails` (including `validBefore` as string).
 * @param {IOtpRawToken} token - Raw token from OTP auth endpoints.
 * @returns {ITokenDetails} Token details usable by the login flow.
 */
export function normalizeOtpRawTokenToTokenDetails(token: IOtpRawToken): ITokenDetails {
  return {
    id: token.id,
    loginType: token.loginType,
    refreshKey: token.refreshKey,
    token: token.token,
    userId: token.userId,
    userType: token.userType,
    validBefore: `${token.validBefore ?? ''}`,
  };
}

/**
 * Maps OTP API session bean to login token content.
 * @param {IOtpApiUserBeanSession} session - Session from OTP verify or select-user.
 * @returns {ILoginTokenContent} Content shape for downstream login handling.
 */
export function mapOtpApiUserBeanSessionToLoginContent(session: IOtpApiUserBeanSession): ILoginTokenContent {
  return {
    token: normalizeOtpRawTokenToTokenDetails(session.token),
    user: session.user,
  };
}

/**
 * Maps OTP session to full login token response with an optional API message code.
 * @param {IOtpApiUserBeanSession} session - Session from OTP verify or select-user.
 * @param {string | null} [message] - API message; defaults to `AUTH_OTP_VERIFIED`.
 * @returns {ILoginTokenResponse} Wrapped login token response.
 */
export function mapOtpApiUserBeanSessionToLoginTokenResponse(session: IOtpApiUserBeanSession, message: string | null = 'AUTH_OTP_VERIFIED'): ILoginTokenResponse {
  return {
    message,
    content: mapOtpApiUserBeanSessionToLoginContent(session),
  };
}

/** Body shape for a successful `POST /auth/v2/otp/select-user` response before mapping to login types. */
export interface IOtpSelectUserHttpEnvelope {
  readonly content: IOtpApiUserBeanSession;
  readonly message: string | null;
}

/**
 * Maps the select-user HTTP response to `ILoginTokenResponse`.
 * @param {IOtpSelectUserHttpEnvelope} resp - JSON body from OTP select-user.
 * @returns {ILoginTokenResponse} Wrapped login token response.
 */
export function mapOtpSelectUserHttpResponseToLoginTokenResponse(resp: IOtpSelectUserHttpEnvelope): ILoginTokenResponse {
  return {
    message: resp.message,
    content: mapOtpApiUserBeanSessionToLoginContent(resp.content),
  };
}
